import './App.css';
import {HomePage} from "./pages/HomePage";
import {RecentTrades} from "./pages/RecentTrades";
import React, { useState, useEffect } from 'react';
import { Routes,
  Route, Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from './stateManagement/AuthContext';
import { AuthModal } from './components/AuthModal';
import { Button, Box, Typography, AppBar, Toolbar, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AboutPage from './pages/AboutPage';
import Glossary from './pages/Glossary';
import AffliatesPage from './pages/AffliatesPage';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { fetchCSRFToken } from './apis/APICalls';
import Footer from './components/Footer';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#ff5252' }, // branding red-orange
    success: { main: '#4caf50' },
    error: { main: '#f44336' },
    text: { primary: '#1a1a1a', secondary: '#4d4d4d' },
    background: { default: '#fafafa', paper: '#ffffff' },
    grey: {
      500: '#757575'
    }
  },
  spacing: 8, // 8px grid system
  shape: {
    borderRadius: 4 // base; buttons use 4 via overrides, cards use 8
  },
  typography: {
    fontFamily: ['Inter', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    h1: { fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.25 },
    h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.35 },
    h5: { fontWeight: 600, fontSize: '1.125rem' },
    body1: { fontSize: 16, lineHeight: 1.6 },
    body2: { fontSize: 14, lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 600 }
  },
  transitions: {
    duration: { shorter: 200, short: 200, standard: 200 },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0.0, 0.2, 1)'
    }
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0,0,0,0.06)',
    '0px 2px 4px rgba(0,0,0,0.08)',
    '0px 4px 8px rgba(0,0,0,0.10)',
    '0px 8px 16px rgba(0,0,0,0.12)',
    ...Array(20).fill('0px 8px 16px rgba(0,0,0,0.12)')
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          transition: 'all 200ms ease-in-out',
          paddingLeft: 16,
          paddingRight: 16
        }
      }
    },
    MuiPaper: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'box-shadow 200ms ease-in-out, transform 200ms ease-in-out'
        }
      }
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 8 } }
    },
    MuiAppBar: {
      styleOverrides: { root: { borderRadius: 0 } }
    }
  }
});

function App() {
  const { isAuthenticated, user, logout } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const headerTitle = React.useMemo(() => {
    switch (location.pathname) {
      case '/':
        return 'Trade-vue: Learn to trade';
      case '/recent':
        return 'Trade-vue: Recent results';
      case '/about':
        return 'Trade-vue: The methodology';
      case '/glossary':
        return 'Trade-vue: Glossary';
      case '/affliates':
        return 'Trade-vue: Affliates';
      default:
        return 'Trade-vue';
    }
  }, [location.pathname]);

  const handleOpenAuthModal = () => setAuthModalOpen(true);
  const handleCloseAuthModal = () => setAuthModalOpen(false);

  useEffect(() => {
    fetchCSRFToken();
  }, []);

  // Render app immediately; auth-dependent UI adjusts as auth resolves

  return (
    <ThemeProvider theme={theme}>
      <div className="app">
          <AppBar position="fixed" sx={{ mb: 2 }}>
            <Toolbar>
              <IconButton color="inherit" edge="start" sx={{ mr: 1, display: { xs: 'inline-flex', md: 'none' } }} onClick={() => setMobileOpen(true)} aria-label="open navigation">
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" component={RouterLink} to="/" sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}>
                {headerTitle}
              </Typography>
              <Box sx={{ display: { xs: 'none', md: 'inline-flex' }, gap: 1 }}>
                <Button color="inherit" component={RouterLink} to="/">Practice</Button>
                <Button color="inherit" component={RouterLink} to="/recent">Recent Results</Button>
                <Button color="inherit" component={RouterLink} to="/#settings">Settings</Button>
                <Button color="inherit" component={RouterLink} to="/about">About</Button>
                <Button color="inherit" component={RouterLink} to="/glossary">Glossary</Button>
                <Button color="inherit" component={RouterLink} to="/affliates">Affliates</Button>
              </Box>
              {isAuthenticated ? (
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                  <Typography sx={{ mr: 2 }}>Welcome, {user?.username || 'User'}!</Typography>
                  <Button color="inherit" onClick={logout} variant="outlined" sx={{ borderColor: 'white', color: 'white' }}>Logout</Button>
                </Box>
              ) : (
                <Button color="inherit" onClick={handleOpenAuthModal} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>Login / Register</Button>
              )}
            </Toolbar>
          </AppBar>
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
            <Box sx={{ width: 240 }} role="presentation" onClick={() => setMobileOpen(false)}>
              <List>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/">
                    <ListItemText primary="Practice" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/recent">
                    <ListItemText primary="Recent Results" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/#settings">
                    <ListItemText primary="Settings" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/about">
                    <ListItemText primary="About" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/glossary">
                    <ListItemText primary="Glossary" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/affliates">
                    <ListItemText primary="Affliates" />
                  </ListItemButton>
                </ListItem>
                {!isAuthenticated && (
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleOpenAuthModal}>
                      <ListItemText primary="Login / Register" />
                    </ListItemButton>
                  </ListItem>
                )}
                {isAuthenticated && (
                  <ListItem disablePadding>
                    <ListItemButton onClick={logout}>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Box>
          </Drawer>
          <Toolbar />

          <Routes>
            <Route exact path='/' element={<HomePage />} />
            <Route path='/recent' element={<RecentTrades />} />
            <Route path='/affliates' element={<AffliatesPage />} />
            <Route path='/about' element={<AboutPage />} />
            <Route path='/glossary' element={<Glossary />} />
          </Routes>

          <AuthModal open={authModalOpen} handleClose={handleCloseAuthModal} />

          <Footer />
        
      </div>
    </ThemeProvider>
  );
}

export default App;
